import React, { useRef, useEffect, FC, memo, useMemo } from "react";

interface ComponentProps {
  glitchColors?: string[];
  glitchSpeed?: number;
  centerVignette?: boolean;
  outerVignette?: boolean;
  smooth?: boolean;
  fullscreen?: boolean;
  performanceMode?: boolean;
}

export const Component: FC<ComponentProps> = memo(({
  glitchColors = ["#2b4539", "#61dca3", "#61b3dc"],
  glitchSpeed = 50,
  centerVignette = false,
  outerVignette = true,
  smooth = true,
  fullscreen = true,
  performanceMode = true,
}) => {
  const offscreenCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const letters = useRef<
    {
      char: string;
      nextChar: string;
      opacity: number;
      targetOpacity?: number;
      opacityProgress?: number;
      transitionProgress: number;
      color: string;
      targetColor: string;
      colorProgress: number;
      isWord: boolean;
      active: boolean;
    }[]
  >([]);
  const grid = useRef({ columns: 0, rows: 0 });
  const context = useRef<CanvasRenderingContext2D | null>(null);
  const offscreenContext = useRef<CanvasRenderingContext2D | null>(null);
  const lastGlitchTime = useRef(Date.now());
  const lastRenderTime = useRef(Date.now());
  const frameSkip = useRef(0);
  const rafId = useRef<number | null>(null);
  const isVisible = useRef(true);
  const wasMinimized = useRef(false);
  const previousWindowDimensions = useRef({ width: 0, height: 0 });

  const colors = useRef({
    wordColors: [
      "#df9fff", "#c77bff", "#b06fff", "#d2b3ff", "#e9c1ff", 
      "#ff9cff", "#f7c8ff", "#c9a2ff", "#b68dff", "#a675ff"
    ],
    symbolColors: [
      "#151029", "#1d1733", "#1a1531", "#201638", "#1a1430", 
      "#231945", "#272040", "#181033", "#12091f", "#1e1736",
      "#161028", "#2c2148", "#1c1434", "#261840", "#1f1538"
    ],
    accentColors: [
      "#5d3d90", "#5939a2", "#4c3680", "#5b357c", "#4e34a3",
      "#6a40c2", "#6a3dc7", "#613d99", "#5d3fb2", "#5836aa"
    ]
  });

  const constants = useMemo(() => {
    return {
      fontSize: 14,
      charWidth: performanceMode ? 16 : 16,
      charHeight: performanceMode ? 32 : 32,
      targetFPS: 60,
      renderFPS: 60,
      glitchInterval: 30,
      charTransitionSpeed: 0.9,
      opacityTransitionSpeed: 0.03,
      letterDensity: performanceMode ? 0.7 : 0.7,
      highlightCount: 100,
      dimCount: 100,
      glow: true,
      glowIntensity: 15,
      fadeDelay: 1000,
      reHighlightDelay: 500,
      cycleEnabled: true,
      cycleChance: 0.5,
      minOpacity: 0.1,
      maxOpacity: 0.5,
      alwaysVisibleLetterCount: 100,
      minVisibleTime: 10000,
    };
  }, [glitchSpeed, performanceMode]);

  const startAnimation = () => {
    const now = performance.now();
    lastGlitchTime.current = now;
    lastRenderTime.current = now;
    
    if (rafId.current) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }
    
    if (letters.current.length === 0 && canvasRef.current) {
      resizeCanvasAndDraw();
    } else if (canvasRef.current) {
      // Сброс состояний букв для восстановления случайной логики подсветки при возвращении видимости
      for (let i = 0; i < letters.current.length; i++) {
        if (letters.current[i]) {
          if (letters.current[i].targetOpacity === undefined) {
            letters.current[i].targetOpacity = letters.current[i].opacity;
          }
          if (letters.current[i].opacityProgress === undefined) {
            letters.current[i].opacityProgress = 1;
          }

          // Ключевые слова оставляем видимыми, обычные буквы сбрасываем для случайного выбора
          if (letters.current[i].isWord) {
            letterState.current[i] = 'visible';
            letters.current[i].targetOpacity = constants.maxOpacity;
            letters.current[i].opacityProgress = 1;
          } else {
            letterState.current[i] = 'hidden';
            nextUpdateTime.current[i] = 0;
            letters.current[i].targetOpacity = constants.minOpacity;
            letters.current[i].opacityProgress = 0;
          }
        }
      }

      drawLetters();
    }
    
    rafId.current = requestAnimationFrame(animate);
  };
  
  const stopAnimation = () => {
    if (rafId.current) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }
  };

  const worker = useRef<Worker | null>(null);
  useEffect(() => {
    if (typeof Worker !== 'undefined' && typeof window !== 'undefined') {
      try {
        if (worker.current) {
          worker.current.terminate();
        }
        
        if (!offscreenCanvasRef.current) {
          offscreenCanvasRef.current = document.createElement('canvas');
        }
      } catch (error) {
        console.error("Error setting up worker:", error);
      }
    }

    return () => {
      if (worker.current) {
        worker.current.terminate();
        worker.current = null;
      }
    };
  }, []);

  const lastRestoreTime = useRef(0);
  const needsStateRecovery = useRef(false);

  const ensureMinimumVisibleLetters = () => {
    if (!letters.current || letters.current.length === 0) return;
    
    const now = performance.now();
    let visibleCount = 0;
    
    for (let i = 0; i < letters.current.length; i++) {
      if (letters.current[i] && 
          letters.current[i].active && 
          letterState.current[i] === 'visible' &&
          letters.current[i].opacity > 0.5) {
        visibleCount++;
      }
    }
    
    if (visibleCount < constants.alwaysVisibleLetterCount) {
      const needToShow = constants.alwaysVisibleLetterCount - visibleCount;
      const candidates = [];
      
      for (let i = 0; i < letters.current.length; i++) {
        if (letters.current[i] && 
            letters.current[i].active && 
            letterState.current[i] === 'hidden') {
          candidates.push(i);
        }
      }
      
      if (candidates.length > 0) {
        for (let i = 0; i < Math.min(needToShow, candidates.length); i++) {
          const randomIndex = Math.floor(Math.random() * candidates.length);
          const letterIndex = candidates[randomIndex];
          
          showLetter(letterIndex, now);
          nextUpdateTime.current[letterIndex] = now + constants.minVisibleTime;
          
          candidates.splice(randomIndex, 1);
        }
      }
    }
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisible.current = document.visibilityState === 'visible';
      
      if (isVisible.current) {
        lastRestoreTime.current = performance.now();
        needsStateRecovery.current = true;
        
        lastGlitchTime.current = performance.now();
        lastRenderTime.current = performance.now();
        
        if (letters.current && letters.current.length > 0) {
          for (let i = 0; i < letters.current.length; i++) {
            if (letters.current[i]) {
              if (letters.current[i].targetOpacity === undefined) {
                letters.current[i].targetOpacity = letters.current[i].opacity;
              }
              if (letters.current[i].opacityProgress === undefined) {
                letters.current[i].opacityProgress = 1;
              }
            }
          }
        }

        // Не вызываем ensureMinimumVisibleLetters() при возвращении видимости,
        // чтобы дать время на естественное восстановление через updateLettersLocal()
        startAnimation();
      } else {
        stopAnimation();
      }
    };
    
    const handleFocus = () => {
      isVisible.current = true;
      
      lastRestoreTime.current = performance.now();
      needsStateRecovery.current = true;
      
      lastGlitchTime.current = performance.now();
      lastRenderTime.current = performance.now();
      
      if (letters.current && letters.current.length > 0) {
        for (let i = 0; i < letters.current.length; i++) {
          if (letters.current[i]) {
            if (letters.current[i].targetOpacity === undefined) {
              letters.current[i].targetOpacity = letters.current[i].opacity;
            }
            if (letters.current[i].opacityProgress === undefined) {
              letters.current[i].opacityProgress = 1;
            }
          }
        }
      }

      // Не вызываем ensureMinimumVisibleLetters() при возвращении фокуса,
      // чтобы дать время на естественное восстановление через updateLettersLocal()
      startAnimation();
    };
    
    const handleBlur = () => {
      setTimeout(() => {
        if (document.visibilityState !== 'visible') {
          isVisible.current = false;
          stopAnimation();
        }
      }, 200);
    };
    
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      const currentHeight = window.innerHeight;
      const prevWidth = previousWindowDimensions.current.width;
      const prevHeight = previousWindowDimensions.current.height;
      
      previousWindowDimensions.current = {
        width: currentWidth,
        height: currentHeight
      };
      
      if (Math.abs(currentWidth - prevWidth) > 10 || Math.abs(currentHeight - prevHeight) > 10) {
        resizeCanvasAndDraw();
        
        lastGlitchTime.current = performance.now();
        lastRenderTime.current = performance.now();
        startAnimation();
      }
    };
    
    previousWindowDimensions.current = {
      width: window.innerWidth,
      height: window.innerHeight
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('resize', handleResize);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('resize', handleResize);
    };
  }, [constants.fadeDelay, constants.minVisibleTime, constants.alwaysVisibleLetterCount, constants.maxOpacity, constants.minOpacity]);

  const getRandomChar = () => {
    const lettersAndSymbols = "ARCIUM_MPC_CERBERUS_MANTICORE_CLUSTER_BYZANTINE_COMPUTATION_UNITS_CUS_BFT_ARX_RUST_MXE_TRUSTLESS!@#$&*()-=_+[]{}<>,0123456789".split('');
    return lettersAndSymbols[
      Math.floor(Math.random() * lettersAndSymbols.length)
    ];
  };

  const getRandomSymbolColor = () => {
    if (Math.random() < 0.05) {
      return colors.current.accentColors[Math.floor(Math.random() * colors.current.accentColors.length)];
    }
    return colors.current.symbolColors[Math.floor(Math.random() * colors.current.symbolColors.length)];
  };

  const getRandomWordColor = () => {
    return colors.current.wordColors[Math.floor(Math.random() * colors.current.wordColors.length)];
  };

  const colorCache = useRef(new Map<string, {r: number, g: number, b: number}>());
  useEffect(() => {
    const allColors = [...colors.current.wordColors, ...colors.current.symbolColors, ...colors.current.accentColors];
    
    allColors.forEach(color => {
      if (!colorCache.current.has(color)) {
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        const processedColor = color.replace(shorthandRegex, (m: string, r: string, g: string, b: string) => r + r + g + g + b + b);
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(processedColor);
        
        if (result) {
          const rgb = {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          };
          
          colorCache.current.set(color, rgb);
        }
      }
    });
  }, [colors]);

  const hexToRgb = (hex: string) => {
    if (colorCache.current.has(hex)) {
      return colorCache.current.get(hex);
    }
    
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (_m, r, g, b) => r + r + g + g + b + b);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    
    if (result) {
      const rgb = {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
      };
      
      colorCache.current.set(hex, rgb);
      return rgb;
        }
    
    return null;
  };

  const interpolateColor = (
    start: { r: number; g: number; b: number },
    end: { r: number; g: number; b: number },
    factor: number
  ) => {
    const r = Math.round(start.r + (end.r - start.r) * factor);
    const g = Math.round(start.g + (end.g - start.g) * factor);
    const b = Math.round(start.b + (end.b - start.b) * factor);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const lastColorUpdate = useRef<number[]>([]);
  const letterState = useRef<string[]>([]);
  const nextUpdateTime = useRef<number[]>([]);

  useEffect(() => {
    if (letters.current.length > 0) {
      if (lastColorUpdate.current.length !== letters.current.length) {
        lastColorUpdate.current = new Array(letters.current.length).fill(0);
      }
      if (letterState.current.length !== letters.current.length) {
        letterState.current = new Array(letters.current.length).fill('hidden');
      }
      if (nextUpdateTime.current.length !== letters.current.length) {
        nextUpdateTime.current = new Array(letters.current.length).fill(0);
      }
    }
  }, [letters.current.length]);

  const calculateGrid = (width: number, height: number) => {
    const columns = Math.ceil(width / constants.charWidth);
    const rows = Math.ceil(height / constants.charHeight);
    return { columns, rows };
  };

  const initializeLetters = (columns: number, rows: number) => {
    grid.current = { columns, rows };
    const totalLetters = columns * rows;
    
    if (letters.current.length !== totalLetters) {
      letters.current = new Array(totalLetters);
      lastColorUpdate.current = new Array(totalLetters).fill(0);
      letterState.current = new Array(totalLetters).fill('hidden');
      nextUpdateTime.current = new Array(totalLetters).fill(0);
      
      for (let i = 0; i < totalLetters; i++) {
        const shouldShowLetter = Math.random() < constants.letterDensity;
        
        letters.current[i] = {
          char: shouldShowLetter ? getRandomChar() : ' ',
          nextChar: shouldShowLetter ? getRandomChar() : ' ',
          opacity: shouldShowLetter ? constants.minOpacity : 0,
          targetOpacity: shouldShowLetter ? constants.minOpacity : 0,
          opacityProgress: 1,
          transitionProgress: 1,
          color: shouldShowLetter ? getRandomSymbolColor() : '#000000',
          targetColor: shouldShowLetter ? getRandomSymbolColor() : '#000000',
          colorProgress: 1,
          isWord: false,
          active: shouldShowLetter
        };
      }
    } else {
      for (let i = 0; i < totalLetters; i++) {
        const shouldShowLetter = Math.random() < constants.letterDensity;
        lastColorUpdate.current[i] = 0;
        letterState.current[i] = 'hidden';
        nextUpdateTime.current[i] = 0;
        
        if (!letters.current[i]) {
          letters.current[i] = {
            char: shouldShowLetter ? getRandomChar() : ' ',
            nextChar: shouldShowLetter ? getRandomChar() : ' ',
            opacity: shouldShowLetter ? constants.minOpacity : 0,
            targetOpacity: shouldShowLetter ? constants.minOpacity : 0,
            opacityProgress: 1,
            transitionProgress: 1,
            color: shouldShowLetter ? getRandomSymbolColor() : '#000000',
            targetColor: shouldShowLetter ? getRandomSymbolColor() : '#000000',
            colorProgress: 1,
            isWord: false,
            active: shouldShowLetter
          };
        } else if (letters.current[i]) {
          letters.current[i].char = shouldShowLetter ? getRandomChar() : ' ';
          letters.current[i].nextChar = shouldShowLetter ? getRandomChar() : ' ';
          letters.current[i].opacity = shouldShowLetter ? constants.minOpacity : 0;
          letters.current[i].targetOpacity = shouldShowLetter ? constants.minOpacity : 0;
          letters.current[i].opacityProgress = 1;
          letters.current[i].transitionProgress = 1;
          letters.current[i].color = shouldShowLetter ? getRandomSymbolColor() : '#000000';
          letters.current[i].targetColor = shouldShowLetter ? getRandomSymbolColor() : '#000000';
          letters.current[i].colorProgress = 1;
          letters.current[i].isWord = false;
          letters.current[i].active = shouldShowLetter;
        }
      }
    }
    
    const words = [
      "ARCIUM", "MPC", "CERBERUS", "MANTICORE", "CLUSTER", 
      "BYZANTINE", "COMPUTATION", "UNITS", "CUS", "BFT", 
      "ARX", "RUST", "MXE", "TRUSTLESS"
    ];
    
    const rowIncrement = performanceMode ? 4 : 3;
    for (let i = 0; i < rows; i += rowIncrement) {
      const wordsPerRow = performanceMode ? 2 : Math.floor(Math.random() * 2) + 1;
      
      for (let wordIdx = 0; wordIdx < wordsPerRow; wordIdx++) {
        const word = words[Math.floor(Math.random() * words.length)];
        const maxStartCol = columns - word.length - 1;
        
        if (maxStartCol <= 0) continue;
        
        const availableWidth = columns - word.length;
        const segmentWidth = availableWidth / wordsPerRow;
        const startColMin = Math.floor(wordIdx * segmentWidth);
        const startColMax = Math.floor((wordIdx + 1) * segmentWidth - word.length);
        
        const startCol = startColMin + Math.floor(Math.random() * (startColMax - startColMin + 1));
        const row = i;
        
        for (let j = 0; j < word.length; j++) {
          const index = row * columns + startCol + j;
          if (index < totalLetters && letters.current[index]) {
            letters.current[index].char = word[j];
            letters.current[index].nextChar = word[j];
            letters.current[index].opacity = 1;
            letters.current[index].transitionProgress = 1;
            letters.current[index].color = getRandomWordColor();
            letters.current[index].targetColor = getRandomWordColor();
            letters.current[index].colorProgress = 1;
            letters.current[index].isWord = true;
            letters.current[index].active = true;
          }
        }
      }
    }
    
    const extraWordCount = performanceMode ? Math.floor(rows / 6) : Math.floor(rows / 4);
    for (let i = 0; i < extraWordCount; i++) {
      const word = words[Math.floor(Math.random() * words.length)];
      const startCol = Math.floor(Math.random() * (columns - word.length));
      const row = Math.floor(Math.random() * rows);
      
        for (let j = 0; j < word.length; j++) {
          const index = row * columns + startCol + j;
          if (index < totalLetters && letters.current[index]) {
            letters.current[index].char = word[j];
            letters.current[index].nextChar = word[j];
            letters.current[index].opacity = 1;
            letters.current[index].transitionProgress = 1;
            letters.current[index].color = getRandomWordColor();
            letters.current[index].targetColor = getRandomWordColor();
            letters.current[index].colorProgress = 1;
            letters.current[index].isWord = true;
            letters.current[index].active = true;
          }
        }
    }
  };

  const drawLetters = () => {
    const canvas = canvasRef.current;
    const ctx = context.current;
    const offscreenCanvas = offscreenCanvasRef.current;
    const offCtx = offscreenContext.current;
    
    if (!canvas || !ctx || !offscreenCanvas || !offCtx || letters.current.length === 0) return;
    
    const { width, height } = canvas.getBoundingClientRect();
    
    offCtx.clearRect(0, 0, width, height);
    
    const roundedWidth = Math.round(width);
    const roundedHeight = Math.round(height);
    
    const symbolsByColor = new Map<string, {x: number, y: number, char: string, opacity: number}[]>();
    const wordsByColor = new Map<string, {x: number, y: number, char: string, opacity: number}[]>();
    
    const buffer = 1;
    const minVisibleX = -constants.charWidth * buffer;
    const maxVisibleX = roundedWidth + constants.charWidth * buffer;
    const minVisibleY = -constants.charHeight * buffer;
    const maxVisibleY = roundedHeight + constants.charHeight * buffer;
    
    const startCol = Math.max(0, Math.floor(minVisibleX / constants.charWidth));
    const endCol = Math.min(grid.current.columns, Math.ceil(maxVisibleX / constants.charWidth));
    const startRow = Math.max(0, Math.floor(minVisibleY / constants.charHeight));
    const endRow = Math.min(grid.current.rows, Math.ceil(maxVisibleY / constants.charHeight));
    
    for (let row = startRow; row < endRow; row++) {
      for (let col = startCol; col < endCol; col++) {
        const i = row * grid.current.columns + col;
        if (i >= letters.current.length) continue;
        
      const letterData = letters.current[i];
        if (!letterData.active) continue;
        
        const x = Math.round(col * constants.charWidth);
        const y = Math.round(row * constants.charHeight);
        
        if (letterData.isWord) {
          if (!wordsByColor.has(letterData.color)) {
            wordsByColor.set(letterData.color, []);
          }
          wordsByColor.get(letterData.color)!.push({ 
            x, 
            y, 
            char: letterData.char,
            opacity: letterData.opacity
          });
        } else {
          if (!symbolsByColor.has(letterData.color)) {
            symbolsByColor.set(letterData.color, []);
          }
          symbolsByColor.get(letterData.color)!.push({ 
            x, 
            y, 
            char: letterData.char,
            opacity: letterData.opacity
          });
        }
      }
    }
    
    offCtx.font = `${constants.fontSize}px monospace`;
    symbolsByColor.forEach((symbols, color) => {
      offCtx.fillStyle = color;
      
      if (constants.glow && color.includes('ff')) {
        offCtx.shadowColor = color;
        offCtx.shadowBlur = constants.glowIntensity;
      } else {
        offCtx.shadowBlur = 0;
      }
      
      for (const { x, y, char, opacity } of symbols) {
          offCtx.globalAlpha = opacity;
          offCtx.fillText(char, x, y);
          }
      
          offCtx.globalAlpha = 1;
    });
    
    offCtx.font = `bold ${constants.fontSize + 2}px monospace`;
    wordsByColor.forEach((words, color) => {
      offCtx.fillStyle = color;
      
      if (constants.glow) {
        offCtx.shadowColor = color;
        offCtx.shadowBlur = constants.glowIntensity;
        offCtx.shadowOffsetX = 0;
        offCtx.shadowOffsetY = 0;
      }
      
      for (const { x, y, char, opacity } of words) {
        offCtx.globalAlpha = opacity;
        offCtx.fillText(char, x, y);
      }
      
      offCtx.globalAlpha = 1;
    });
    
    offCtx.shadowBlur = 0;
    
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(offscreenCanvas, 0, 0);
  };
  
  const resizeCanvasAndDraw = () => {
    const canvas = canvasRef.current;
    const offscreenCanvas = offscreenCanvasRef.current;
    
    if (!canvas || !offscreenCanvas) return;

    const dpr = performanceMode ? 1 : (window.devicePixelRatio || 1);
    let width, height;

    if (fullscreen) {
      width = window.innerWidth;
      height = window.innerHeight;
      
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      offscreenCanvas.width = width * dpr;
      offscreenCanvas.height = height * dpr;
      
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    } else {
      const parent = canvas.parentElement;
      if (!parent) return;

      const rect = parent.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      offscreenCanvas.width = width * dpr;
      offscreenCanvas.height = height * dpr;
      
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    }

    if (context.current) {
      context.current.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    if (offscreenContext.current) {
      offscreenContext.current.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    const { columns, rows } = calculateGrid(width, height);
    
    const needFullReset = !letters.current.length || 
                          columns * rows !== letters.current.length;
    
    if (needFullReset) {
      initializeLetters(columns, rows);
    } else {
      updateLettersVisibility(columns, rows);
    }
    
    drawLetters();

    previousWindowDimensions.current = {
      width: window.innerWidth,
      height: window.innerHeight
    };
  };

  const updateLettersVisibility = (columns: number, rows: number) => {
    const oldGrid = { ...grid.current };
    grid.current = { columns, rows };
    
    const totalCells = columns * rows;
    
    for (let i = 0; i < totalCells; i++) {
      if (i < letters.current.length) {
        const row = Math.floor(i / columns);
        const col = i % columns;
        
        const isVisible = row < rows && col < columns;
        if (letters.current[i]) {
          letters.current[i].active = isVisible && letters.current[i].char !== ' ';
        }
      }
    }
  };

  const frameDuration = useRef(0);
  const frameStartTime = useRef(0);
  const shouldSkipFrame = useRef(false);

  const highlightedLetterCount = useRef(0);
  const frameCount = useRef(0);

  const showLetter = (index: number, now: number) => {
    if (!letters.current[index] || !letters.current[index].active) return false;
    
    letters.current[index].targetColor = colors.current.wordColors[
      Math.floor(Math.random() * colors.current.wordColors.length)
    ];
    letters.current[index].targetOpacity = constants.maxOpacity;
    letters.current[index].opacityProgress = 0;
            letters.current[index].colorProgress = 0;
    lastColorUpdate.current[index] = now;
    letterState.current[index] = 'visible';
    
    nextUpdateTime.current[index] = now + constants.fadeDelay;
    
    return true;
  };

  const hideLetter = (index: number, now: number) => {
    if (!letters.current[index] || !letters.current[index].active) return false;
    
    letters.current[index].targetColor = colors.current.symbolColors[
      Math.floor(Math.random() * colors.current.symbolColors.length)
    ];
    letters.current[index].targetOpacity = constants.minOpacity;
    letters.current[index].opacityProgress = 0;
    letters.current[index].colorProgress = 0;
    lastColorUpdate.current[index] = now;
    letterState.current[index] = 'hidden';
    
    if (constants.cycleEnabled && Math.random() < constants.cycleChance) {
      nextUpdateTime.current[index] = now + constants.reHighlightDelay;
            } else {
      nextUpdateTime.current[index] = 0;
    }
    
    return true;
  };

  const updateLettersLocal = () => {
    if (!letters.current || letters.current.length === 0) return false;
    
    const totalLetters = letters.current.length;
    let updated = false;
    const now = performance.now();
    frameCount.current++;
    
    for (let i = 0; i < constants.highlightCount; i++) {
      const index = Math.floor(Math.random() * totalLetters);
      
      if (index < totalLetters && 
          letters.current[index] && 
          letters.current[index].active && 
          !letters.current[index].isWord &&
          letterState.current[index] === 'hidden' &&
          nextUpdateTime.current[index] === 0) {
        
        if (letters.current[index].targetOpacity === undefined) {
          letters.current[index].targetOpacity = letters.current[index].opacity;
        }
        if (letters.current[index].opacityProgress === undefined) {
          letters.current[index].opacityProgress = 1;
        }
        
        updated = showLetter(index, now) || updated;
      }
    }
    
    return updated;
  };

  const handleSmoothTransitions = () => {
    const now = performance.now();
    lastRenderTime.current = now;
    
        let needsRedraw = false;
    
    for (let i = 0; i < letters.current.length; i++) {
      const letter = letters.current[i];
      
      if (letter && letter.active) {
        if (letter.targetOpacity === undefined) {
          letter.targetOpacity = letter.opacity;
        }
        if (letter.opacityProgress === undefined) {
          letter.opacityProgress = 1;
        }
        
        if (nextUpdateTime.current[i] > 0 && now >= nextUpdateTime.current[i]) {
          if (letterState.current[i] === 'visible') {
            hideLetter(i, now);
            needsRedraw = true;
          } else {
            showLetter(i, now);
            needsRedraw = true;
          }
        }
        
        if (letter.colorProgress < 1) {
          letter.colorProgress += constants.charTransitionSpeed;
            if (letter.colorProgress > 1) letter.colorProgress = 1;
            
            const startRgb = hexToRgb(letter.color);
            const endRgb = hexToRgb(letter.targetColor);
            
            if (startRgb && endRgb) {
              letter.color = interpolateColor(startRgb, endRgb, letter.colorProgress);
              needsRedraw = true;
            }
          }
          
        if (letter.opacityProgress < 1) {
          letter.opacityProgress += constants.opacityTransitionSpeed;
          if (letter.opacityProgress > 1) letter.opacityProgress = 1;
          
          const startOpacity = letter.opacity;
          const endOpacity = letter.targetOpacity;
          
          letter.opacity = startOpacity + (endOpacity - startOpacity) * letter.opacityProgress;
            needsRedraw = true;
          }
        }
    }
    
    return needsRedraw;
    };

  const animate = () => {
    try {
      if (!isVisible.current || !canvasRef.current) {
      rafId.current = requestAnimationFrame(animate);
      return;
    }
    
      const now = performance.now();
      
      if (needsStateRecovery.current && now - lastRestoreTime.current > 500) {
        ensureMinimumVisibleLetters();
        needsStateRecovery.current = false;
      }
      
      const timeSinceLastFrame = now - lastRenderTime.current;
      if (timeSinceLastFrame > 1000) {
        lastGlitchTime.current = now;
      lastRenderTime.current = now;

        // Сброс состояний происходит в startAnimation() при возвращении видимости
        // Здесь только обновляем opacityProgress для плавных переходов
        for (let i = 0; i < letters.current.length; i++) {
          if (letters.current[i]?.active) {
            if (letters.current[i].targetOpacity === undefined) {
              letters.current[i].targetOpacity = letters.current[i].opacity;
            }
            if (letters.current[i].opacityProgress === undefined) {
              letters.current[i].opacityProgress = 1;
            }

            letters.current[i].opacityProgress = 0;
          }
        }

        ensureMinimumVisibleLetters();
      }
      
      const timeSinceLastUpdate = now - lastGlitchTime.current;
      if (timeSinceLastUpdate >= constants.glitchInterval) {
        updateLettersLocal();
        lastGlitchTime.current = now;
        
        if (frameCount.current % 100 === 0) {
          ensureMinimumVisibleLetters();
        }
      }
      
      handleSmoothTransitions();
      
      drawLetters();
      
      lastRenderTime.current = now;
      
      frameCount.current++;
    } catch (error) {
      console.error('Error in animation loop:', error);
    }
    
    rafId.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    context.current = canvas.getContext('2d', { 
      alpha: false,
    });
    
    if (offscreenCanvasRef.current) {
      offscreenContext.current = offscreenCanvasRef.current.getContext('2d', {
        alpha: false,
      });
    } else {
      const offCanvas = document.createElement('canvas');
      offscreenCanvasRef.current = offCanvas;
      offscreenContext.current = offCanvas.getContext('2d', {
        alpha: false,
      });
    }
    
    resizeCanvasAndDraw();
    
    lastGlitchTime.current = performance.now();
    lastRenderTime.current = performance.now();
    
    if (rafId.current) {
      cancelAnimationFrame(rafId.current);
    }
    
    rafId.current = requestAnimationFrame(animate);

    let resizeTimeout: NodeJS.Timeout;
    let lastResizeTime = 0;
    
    const handleResize = () => {
      const now = Date.now();
      const currentWidth = window.innerWidth;
      const currentHeight = window.innerHeight;
      const prevWidth = previousWindowDimensions.current.width;
      const prevHeight = previousWindowDimensions.current.height;
      
      if ((prevWidth <= 100 || prevHeight <= 100) && 
          (currentWidth > 100 && currentHeight > 100)) {
        wasMinimized.current = true;
        isVisible.current = true;
        startAnimation();
      }
      
      previousWindowDimensions.current = {
        width: currentWidth,
        height: currentHeight
      };
      
      if (now - lastResizeTime < 200) {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
          resizeCanvasAndDraw();
          startAnimation();
          lastResizeTime = Date.now();
        }, 200);
        return;
      }
      
      lastResizeTime = now;
        resizeCanvasAndDraw();
      startAnimation();
    };
    
    window.addEventListener("resize", handleResize, { passive: true });
    
    document.addEventListener('fullscreenchange', startAnimation);
    document.addEventListener('webkitfullscreenchange', startAnimation);
    document.addEventListener('mozfullscreenchange', startAnimation);
    document.addEventListener('MSFullscreenChange', startAnimation);

    return () => {
      stopAnimation();
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimeout);
      
      document.removeEventListener('fullscreenchange', startAnimation);
      document.removeEventListener('webkitfullscreenchange', startAnimation);
      document.removeEventListener('mozfullscreenchange', startAnimation);
      document.removeEventListener('MSFullscreenChange', startAnimation);
    };
  }, [glitchSpeed, smooth, performanceMode, fullscreen, constants]);

  const containerStyle: React.CSSProperties = fullscreen
    ? {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "#000000",
        overflow: "hidden",
        zIndex: -1,
      }
    : {
    position: "relative",
    width: "100%",
    height: "100%",
    backgroundColor: "#000000",
    overflow: "hidden",
  };

  const canvasStyle: React.CSSProperties = {
    display: "block",
    width: "100%",
    height: "100%",
    willChange: "transform",
    backfaceVisibility: "hidden",
  };

  const outerVignetteStyle = useMemo<React.CSSProperties>(() => ({
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    background: "radial-gradient(circle, rgba(0,0,0,0) 60%, rgba(0,0,0,1) 100%)",
    willChange: "opacity",
  }), []);

  const centerVignetteStyle = useMemo<React.CSSProperties>(() => ({
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    background: "radial-gradient(circle, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 60%)",
    willChange: "opacity",
  }), []);

  return (
    <div style={containerStyle}>
      <canvas ref={canvasRef} style={canvasStyle} />
      {outerVignette && <div style={outerVignetteStyle}></div>}
      {centerVignette && <div style={centerVignetteStyle}></div>}
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.fullscreen === nextProps.fullscreen && 
    prevProps.performanceMode === nextProps.performanceMode &&
    prevProps.glitchSpeed === nextProps.glitchSpeed
  );
});